import {endsWith, memoize, filter, mapValues} from 'lodash';
import derefMapboxStyle from '../../mapbox/derefMapboxStyle';
import fetchJSON from '../../fetchJSON';
import opacifyMapboxStyle from '../../mapbox/opacifyMapboxStyle';
import React from 'react';
import resolveGaiaURL from './resolveGaiaURL';
import resolveMapboxURL from '../../mapbox/resolveMapboxURL';

interface FetchLayerMapboxStyleOptions {
    preview?: boolean;
    uniqueTilecacheKey?: string;
}

async function fetchLayerMapboxStyle(
    layerTypes: {[key: string]: LayerType},
    layer: Layer,
    options: FetchLayerMapboxStyleOptions
) {
    return opacifyMapboxStyle(
        layer.opacity,
        await fetchLayerTypeMapboxStyle(layerTypes, layer, options)
    );
}

export default memoize(
    fetchLayerMapboxStyle,
    (layerTypes, layer, options) => JSON.stringify([layer, options])
);

export async function fetchLayerTypeMapboxStyle(
    layerTypes: {[key: string]: LayerType},
    layer: Layer,
    {preview = false, uniqueTilecacheKey}: FetchLayerMapboxStyleOptions = {}
): Promise<mapboxgl.Style> {
    const layerType = layerTypes[layer.type];
    return {
        sources: {},
        layers: [],
        ...!layerType.gl_style_url || (preview && layerType.tileUrlPreview) ?
            await fetchRasterLayerTypeMapboxStyle(layerType, {preview}) :
            await fetchVectorLayerTypeMapboxStyle(layerType, layerTypes),
        metadata: {...layerType, ...uniqueTilecacheKey && {uniqueTilecacheKey}},
        name: layerType.title,
        version: 8,
        zoom: layerType.demoZoom || 1,
    };
}

async function fetchVectorLayerTypeMapboxStyle(
    type: LayerType,
    types: {[key: string]: LayerType},
): Promise<mapboxgl.Style> {
    if (!type.gl_style_url) throw new Error();

    const url = resolveMapboxURL(type.gl_style_url);
    const style = derefMapboxStyle(await fetchJSON.memoized(url));

    return {
        ...style,
        layers: filter(style.layers || [], layer => type.basemap || layer.type !== 'background'),
        sources: mapValues(style.sources || {}, (source: any) => {
            if (!source.tiles) return source;
            return {
                ...source,
                tiles: source.tiles.map(resolveGaiaURL.bind(null, types))
            };
        })
    };
}


async function fetchRasterLayerTypeMapboxStyle(
    type: LayerType,
    {preview}: FetchLayerMapboxStyleOptions
) {

    let tileUrl: string;
    let isHD = false;
    if (preview && type.tileUrlPreview) {
        tileUrl = type.tileUrlPreview;
    } else if (type.tileUrlHD && window.devicePixelRatio > 1) {
        tileUrl = type.tileUrlHD;
        isHD = true;
    } else {
        tileUrl = type.tileUrl;
    }

    return {
        sources: {
            source: {
                type: 'raster',
                attribution: type.attribution,
                bounds: [
                    type.swLon,
                    type.swLat,
                    type.neLon,
                    type.neLat
                ],
                scheme: type.reversedYAxis || endsWith(type.tileUrl, '?earthnc') ? 'tms' : 'xyz',
                tiles: [applyCORSProxyURL(tileUrl, type)],
                minzoom: type.minZoom,
                maxzoom: type.maxZoom,
                ...isHD && type.tileSizeHD && {tileSize: type.tileSizeHD / 2},
                ...!isHD && type.tileSize && {tileSize: type.tileSize},
            }
        },
        layers: [{
            id: 'layer',
            type: 'raster',
            source: 'source'
        }]
    } as Partial<mapboxgl.Style>;
}

function applyCORSProxyURL(url: string, {supportsCORS}: LayerType) {
    if (supportsCORS) return url;

    return Gaia.corsProxyUrl + encodeURIComponent(url)
        .replace(encodeURIComponent('{z}'), '{z}')
        .replace(encodeURIComponent('{x}'), '{x}')
        .replace(encodeURIComponent('{y}'), '{y}');
}
